import React, { useEffect, useRef, useState } from "react";
import { Headphones, Send, LifeBuoy, Bot, User as UserIcon, CheckCircle2, RotateCcw, Plus, BookOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { PageCard, PillBtn, Modal, EmptyState } from "./common";

const STATUS = { open: "bg-amber-100 text-amber-800", answered: "bg-sky-100 text-sky-700", closed: "bg-slate-200 text-slate-600" };

export default function HelpDeskPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState(isAdmin ? "tickets" : "assistant");
  const [ticketDraft, setTicketDraft] = useState(null);
  const tabs = isAdmin ? [["tickets", "All tickets"], ["assistant", "Assistant"], ["kb", "Knowledge base"]] : [["assistant", "Ask the assistant"], ["tickets", "My tickets"]];
  return (
    <Layout>
      <div className="p-4 lg:p-6 max-w-5xl mx-auto">
        <PageCard Icon={Headphones} title="Help desk" subtitle={isAdmin ? "Reply to members' tickets and keep the assistant's knowledge up to date." : "Ask anything about the dashboard. If the assistant can't solve it, raise a ticket to the admin."} testid="helpdesk-page"
          actions={!isAdmin && <PillBtn onClick={() => setTicketDraft({ subject: "", message: "" })} data-testid="helpdesk-new-ticket"><Plus className="w-4 h-4" /> Raise a ticket</PillBtn>} />
        <div className="flex gap-2 mb-4">{tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`helpdesk-tab-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>)}</div>
        {tab === "assistant" && <Assistant onEscalate={(q) => setTicketDraft({ subject: q.slice(0, 80), message: q })} />}
        {tab === "tickets" && <Tickets isAdmin={isAdmin} />}
        {tab === "kb" && isAdmin && <KnowledgeBase />}
      </div>
      {ticketDraft && <NewTicket draft={ticketDraft} onClose={() => setTicketDraft(null)} onDone={() => { setTicketDraft(null); setTab("tickets"); }} />}
    </Layout>
  );
}

function Assistant({ onEscalate }) {
  const [msgs, setMsgs] = useState([{ from: "bot", text: "Hi! I know every feature of this dashboard. What do you need help with?" }]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const ask = async (e) => {
    e.preventDefault();
    const question = q.trim();
    if (!question) return;
    setQ(""); setBusy(true);
    setMsgs((m) => [...m, { from: "user", text: question }]);
    try {
      const r = await api.post("/helpdesk/ask", { question });
      setMsgs((m) => [...m, { from: "bot", question, answers: r.data.answers }]);
    } catch { setMsgs((m) => [...m, { from: "bot", question, answers: [] }]); }
    setBusy(false);
  };
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm flex flex-col h-[60vh]" data-testid="helpdesk-assistant">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {msgs.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.from === "user" ? "flex-row-reverse" : ""}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.from === "user" ? "bg-slate-200" : "bg-sky-600"}`}>{m.from === "user" ? <UserIcon className="w-4 h-4 text-slate-600" /> : <Bot className="w-4 h-4 text-white" />}</span>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.from === "user" ? "bg-sky-600 text-white" : "bg-slate-50 text-slate-800"}`} data-testid={m.from === "bot" && m.answers?.length ? "assistant-answer" : m.answers ? "assistant-no-answer" : undefined}>
              {m.text}
              {m.answers && (m.answers.length ? (
                <div className="space-y-3">{m.answers.map((a, j) => <div key={a.key}><div className="font-bold">{j === 0 ? "" : "Also: "}{a.title}</div><div className="text-slate-600 mt-0.5 leading-relaxed">{a.body}</div></div>)}
                  <div className="pt-2 border-t border-slate-200 text-xs text-slate-500">Didn't solve it? <button onClick={() => onEscalate(m.question)} className="text-sky-700 font-semibold underline" data-testid="assistant-escalate">Raise a ticket to the admin</button></div>
                </div>
              ) : (
                <div>I couldn't find that in the dashboard guide. <button onClick={() => onEscalate(m.question)} className="text-sky-700 font-semibold underline" data-testid="assistant-escalate">Send it to the admin as a ticket</button> and they'll reply here.</div>
              ))}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={ask} className="p-3 border-t border-slate-100 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. How do I check in a delegate who hasn't paid?" className="input-pill flex-1" data-testid="assistant-input" />
        <PillBtn primary disabled={busy} type="submit" data-testid="assistant-send"><Send className="w-4 h-4" /></PillBtn>
      </form>
    </div>
  );
}

function NewTicket({ draft, onClose, onDone }) {
  const [f, setF] = useState(draft);
  const save = async () => {
    try { await api.post("/helpdesk/tickets", f); toast.success("Ticket sent to the admin — you'll be notified when they reply"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="Raise a ticket" onClose={onClose} testid="new-ticket-modal">
      <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" className="input-pill w-full mb-2" data-testid="ticket-subject" />
      <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Describe the problem (what you did, what happened)" className="input-pill w-full min-h-[120px]" data-testid="ticket-message" />
      <div className="mt-4"><PillBtn primary disabled={!f.subject.trim() || !f.message.trim()} onClick={save} data-testid="ticket-submit"><Send className="w-4 h-4" /> Send to admin</PillBtn></div>
    </Modal>
  );
}

function Tickets({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState(isAdmin ? "open" : "");
  const [current, setCurrent] = useState(null);
  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    setRows(null);
    api.get("/helpdesk/tickets", { params: filter ? { status: filter } : {} })
      .then((r) => { if (id === reqId.current) setRows(r.data); })
      .catch(() => { if (id === reqId.current) setRows([]); });
  };
  useEffect(() => { load(); }, [filter]);
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5" data-testid="helpdesk-tickets">
      <div className="flex gap-2 mb-4">{[["", "All"], ["open", "Open"], ["answered", "Answered"], ["closed", "Closed"]].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} className={`text-xs px-3 py-1 rounded-full font-semibold ${filter === k ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`ticket-filter-${k || "all"}`}>{l}</button>)}</div>
      {!rows ? <div className="py-10 text-center text-slate-400">Loading…</div> : rows.length === 0 ? <EmptyState Icon={LifeBuoy} title="No tickets here" text={isAdmin ? "Members' questions that the assistant couldn't answer land here." : "Tickets you raise appear here with the admin's replies."} /> : (
        <div className="divide-y divide-slate-50">{rows.map((t) => (
          <button key={t.ticket_id} onClick={() => setCurrent(t)} className="w-full flex items-center gap-3 py-3 text-left hover:bg-slate-50 px-2 rounded-lg" data-testid={`ticket-row-${t.ticket_id}`}>
            <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${STATUS[t.status]}`}>{t.status}</span>
            <span className="flex-1 min-w-0"><span className="font-semibold block truncate">{t.subject}</span>{isAdmin && <span className="text-xs text-slate-500">{t.user_name} · {t.user_role_label || t.user_role}</span>}</span>
            <span className="text-xs text-slate-400">{new Date(t.updated_at).toLocaleDateString([], { day: "numeric", month: "short" })}</span>
          </button>
        ))}</div>
      )}
      {current && <TicketThread t={current} isAdmin={isAdmin} onClose={() => setCurrent(null)} onChanged={(fresh) => { setCurrent(fresh); load(); }} />}
    </div>
  );
}

function TicketThread({ t, isAdmin, onClose, onChanged }) {
  const [text, setText] = useState("");
  const send = async () => {
    try { const r = await api.post(`/helpdesk/tickets/${t.ticket_id}/reply`, { text }); setText(""); toast.success(isAdmin ? "Reply sent — member notified by email" : "Reply sent"); onChanged(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (status) => { const r = await api.post(`/helpdesk/tickets/${t.ticket_id}/status`, { status }); toast.success(status === "closed" ? "Ticket closed" : "Ticket reopened"); onChanged(r.data); };
  return (
    <Modal title={t.subject} onClose={onClose} testid="ticket-thread" wide>
      <div className="text-xs text-slate-500 mb-3">{t.user_name} · opened {new Date(t.created_at).toLocaleString()} · <span className={`font-bold uppercase rounded-full px-2 py-0.5 ${STATUS[t.status]}`}>{t.status}</span></div>
      <div className="space-y-3 mb-4 max-h-[45vh] overflow-y-auto">{t.messages.map((m, i) => (
        <div key={i} className={`rounded-2xl px-4 py-3 text-sm ${m.from === "admin" ? "bg-sky-50 ml-8" : "bg-slate-50 mr-8"}`}><div className="text-xs font-bold text-slate-500 mb-1">{m.name}{m.from === "admin" ? " (admin)" : ""} · {new Date(m.at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>{m.text}</div>
      ))}</div>
      {t.status !== "closed" && <div className="flex gap-2"><textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" className="input-pill flex-1 min-h-[70px]" data-testid="ticket-reply-input" /><PillBtn primary disabled={!text.trim()} onClick={send} data-testid="ticket-reply-send"><Send className="w-4 h-4" /></PillBtn></div>}
      {isAdmin && <div className="mt-3">{t.status === "closed"
        ? <PillBtn onClick={() => setStatus("open")} data-testid="ticket-reopen"><RotateCcw className="w-4 h-4" /> Reopen</PillBtn>
        : <PillBtn onClick={() => setStatus("closed")} data-testid="ticket-close"><CheckCircle2 className="w-4 h-4" /> Close ticket</PillBtn>}</div>}
    </Modal>
  );
}

function KnowledgeBase() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", body: "", tags: "" });
  const load = () => api.get("/helpdesk/kb").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { try { await api.post("/helpdesk/kb", f); setF({ title: "", body: "", tags: "" }); toast.success("Added — the assistant now knows this"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const del = async (k) => { await api.delete(`/helpdesk/kb/${k}`); load(); };
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5" data-testid="helpdesk-kb">
      <h4 className="font-bold mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Teach the assistant</h4>
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Question / topic" className="input-pill w-full mb-2" data-testid="kb-title" />
      <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Answer" className="input-pill w-full min-h-[80px] mb-2" data-testid="kb-body" />
      <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="Keywords people might type (comma separated)" className="input-pill w-full mb-3" data-testid="kb-tags" />
      <PillBtn primary disabled={!f.title.trim() || !f.body.trim()} onClick={add} data-testid="kb-save">Add to knowledge base</PillBtn>
      <div className="mt-6 divide-y divide-slate-50">{rows.map((a) => (
        <div key={a.key} className="py-2.5 flex gap-3"><div className="flex-1"><div className="font-semibold text-sm">{a.title}{a.custom && <span className="ml-2 text-[10px] bg-violet-100 text-violet-700 rounded-full px-1.5">ADDED</span>}</div><div className="text-xs text-slate-500 line-clamp-2">{a.body}</div></div>{a.custom && <button onClick={() => del(a.key)} className="text-slate-300 hover:text-rose-500" data-testid={`kb-del-${a.key}`}><Trash2 className="w-4 h-4" /></button>}</div>
      ))}</div>
    </div>
  );
}
